import * as THREE from "../libs/three.js/build/three.module.js";
import { Utils } from "./utils.js";
import { ShowMode } from "./defines.js";
import { TextSprite } from "./TextSprite.js";
import { EventDispatcher } from "./EventDispatcher.js";

// 悬停手型光标：模块级命中集合——多实例各自命中/移开的顺序不可控，
// 集合非空即 pointer、空即恢复原值，与实例处理顺序无关（防 A 实例移开
// 复位覆盖 B 实例命中导致的手型丢失/闪烁）
const hoveredDepthAnnos = new Set();
let _originalCursor = null; // 首次命中前读取 domElement.style.cursor 保存

function _refreshCursor(viewer) {
	if (!viewer || !viewer.renderer) return;
	const dom = viewer.renderer.domElement;
	if (_originalCursor === null) _originalCursor = dom.style.cursor;
	dom.style.cursor = hoveredDepthAnnos.size > 0 ? 'pointer' : _originalCursor;
}

/**
 * AnnotationYdDepth —— WebGL 版标注图标：与点云/高斯有正确的深度遮挡关系。
 *
 * 与 AnnotationYd / AnnotationYd3（DOM <img> 永远叠在 canvas 之上、无遮挡）的区别：
 * 图标是 THREE.Sprite，挂 viewer.scene.scene——该 scene 在三个 renderer 的
 * clearDepth 之前渲染（Images360 / OrientedImages / CameraCone 同通道），材质
 * depthTest=true，被点云与高斯正确遮挡，且随截图导出。零渲染器改动。
 *
 * 用法：
 *   const anno = new Potree.AnnotationYdDepth({
 *       position: [x, y, z],        // 图标锚点（世界坐标）
 *       icon: '.../icon.png',       // 主图标（必填）
 *       iconActive: '.../hover.png',// 高亮图标（可选，配 setHighlighted 换图）
 *       screenSize: 58,             // 屏幕恒定大小时的高度（像素，默认 58；宽度 = 高度 × aspect）
 *       fixedScreenSize: true,      // true=屏幕恒定大小（远处被遮挡但大小不变）；false=世界大小
 *       size: 1,                    // fixedScreenSize=false 时的世界高度
 *       aspect: 1,                  // 宽/高比（默认 1 等比；竖长图标如硬件设备传 0.4）
 *       extraParams: {...},         // 原样透传存取（业务自描述数据）
 *       actions: [{ onclick }],     // 点击回调，回调参数 = 实例本身（对齐 AnnotationYd）
 *       // 或 onClick: (anno) => {}（单函数简写）
 *       name: '...',...
 *   }).addToViewer(viewer);
 *   anno.position.copy(v3);         // 移动：直接改 position（Sprite 位置引用）
 *   anno.setOpacity(0.3);           // 透明度（缺省 1 恢复全显，0.3 = 半透明选中反馈）
 *   anno.setPointImg({name, path}); // 图标旁叠加测点实拍图 + 名称（保持深度遮挡）
 *   anno.showPointImg(bool);        // 测点图显隐
 *   anno.dispose();                 // 销毁（从场景移除、解绑事件、释放纹理）
 *
 * 悬停反馈：鼠标悬停到图标上自动换 iconActive 纹理 + 手型光标（pointer），
 * 移开恢复主图与箭头（对齐 AnnotationYd 的 mouseenter/mouseleave）。类内
 * 每帧检测 inputHandler.mouse（变化时一次单 sprite 求交），不进
 * hoveredElements 拾取池、不做遮挡检测（DOM 版 hover 同样无遮挡概念）。
 * 手型光标经模块级命中集合管理，多实例滑动切换不闪烁；实例隐藏/销毁
 * 自动退出集合并恢复光标。
 *
 * 点击拾取：类内自管——本类作为 inputHandler 的 inputListener 收 mousedown/up，
 * Raycaster 只测自身 sprite，并与点云/高斯拾取距离比较做遮挡检测（图标被
 * 点云/高斯挡住时点击不命中）。sprite 不挂 'click'/'select' 等监听，因此
 * 不会进入 InputHandler 的 hoveredElements 拾取池，不影响点云选中/资源盒。
 */
export class AnnotationYdDepth extends EventDispatcher {
	constructor(args = {}) {
		super();
		this.name = args.name || '';
		this._viewer = null;
		this._updateListener = null;
		this._iconLoaded = false;
		this._disposed = false;

		this._icon = args.icon || null;
		this._iconActive = args.iconActive || null;
		this._iconTexture = null;
		this._iconActiveTexture = null;
		this._texLoader = new THREE.TextureLoader();

		// 大小模式（公开字段，可运行时直接改）：默认屏幕恒定（对齐 DOM 图标的像素视觉）；
		// fixedScreenSize=false 时用世界直径 worldSize
		this.fixedScreenSize = args.fixedScreenSize !== false;
		this.screenSize = args.screenSize || 58;
		this.worldSize = args.size || 1;
		// 宽/高比（width÷height，默认 1 等比）：screenSize/size 语义为"高度"，
		// 宽度 = 高度 × aspect——竖长图标（如硬件设备 32×81 源图）传 0.4 防拉变形。
		// sprite 拾取经 world matrix 求交，非等比 scale 命中区域即显示边界
		this.aspect = args.aspect || 1;
		this.visible = args.visible !== false;
		this._highlighted = false;

		// 业务侧缺口对齐（AnnotationYd 同名行为）：
		// extraParams 原样透传；isAlreadyClicked 是业务的选中标记（复位逻辑读 == false）；
		// opacity 由 setOpacity 维护
		this.extraParams = args.extraParams;
		this.isAlreadyClicked = false;
		this.opacity = 1;

		// 点击回调：actions=[{onclick}] 形态对齐 AnnotationYd；onClick 单函数简写兼容
		this.actions = Array.isArray(args.actions) ? args.actions.slice() : [];
		if (typeof args.onClick === 'function' && this.actions.length === 0) {
			this.actions = [{ onclick: args.onClick }];
		}

		// 测点图三件套状态（惰性建对象，见 _ensurePointImgObjects）
		this._pointImgShown = false;
		this._pointImgTexture = null;

		const material = new THREE.SpriteMaterial({
			transparent: true,
			depthTest: true,     // 遮挡关系的核心：测试场景深度（点云+高斯）
			depthWrite: false,   // 图标不写深度：只被遮挡，不去遮挡他人
			sizeAttenuation: true,
		});

		this.sprite = new THREE.Sprite(material);
		this.sprite.name = 'annotation_yd_depth';
		// raycast 保持原生（供类内点击拾取 Raycaster 命中）。不进 InputHandler
		// 对象拾取池的保证：sprite 自身不挂 'click'/'mouseup' 等监听（无 _listeners），
		// getHoveredElements 只收集带交互监听的对象
		this.sprite.visible = false; // 图标加载完成前不显示

		if (this._icon) {
			this._loadTexture(this._icon, (texture) => {
				this._iconTexture = texture;
				if (!this._highlighted) {
					material.map = texture;
					material.needsUpdate = true;
				}
			});
		}
		if (this._iconActive) {
			this._loadTexture(this._iconActive, (texture) => {
				this._iconActiveTexture = texture;
				if (this._highlighted) {
					material.map = texture;
					material.needsUpdate = true;
				}
			});
		}

		// position 即 Sprite 位置的引用：外部 anno.position.copy(v3) 即移动
		this.position = this.sprite.position;
		if (args.position) {
			if (args.position instanceof THREE.Vector3) {
				this.position.copy(args.position);
			} else if (args.position.length >= 3) {
				this.position.set(...args.position.slice(0, 3));
			}
		}

		// inputHandler 事件按 importance 排序派发（对齐 ScreenBoxSelectTool 模式）
		this.importance = 0;
		this._downScreen = null;
		this._lastHoverMouse = null; // 悬停检测用的上一帧鼠标位置（null = 需重新检测）
		this.addEventListener('mousedown', (e) => this._onMouseDown(e));
		this.addEventListener('mouseup', (e) => this._onMouseUp(e));
	}

	_loadTexture(url, onLoad) {
		this._texLoader.load(url, (texture) => {
			if (this._disposed) {
				texture.dispose();
				return;
			}
			if (THREE.SRGBColorSpace !== undefined) texture.colorSpace = THREE.SRGBColorSpace;
			onLoad(texture);
			if (!this._iconLoaded) {
				this._iconLoaded = true; // 主图标就绪后才开始显示（update 里生效）
			}
		}, undefined, (err) => {
			console.warn('[AnnotationYdDepth] 图标加载失败', url, err);
		});
	}

	// 加入 viewer：挂进天然遮挡通道（viewer.scene.scene 在 clearDepth 前渲染）、
	// 接管每帧更新（屏幕恒定缩放）、注册类内点击拾取。链式返回自身。
	addToViewer(viewer) {
		if (this._viewer) this.dispose();
		this._viewer = viewer;
		viewer.scene.scene.add(this.sprite);
		this._updateListener = () => this._update();
		viewer.addEventListener("update", this._updateListener);
		// 点击拾取：inputHandler 只在 hoveredElements 为空时把鼠标事件派发给
		// inputListeners——sprite 无 _listeners 恒不占 hover 池，本类总能收到事件
		viewer.inputHandler.addInputListener(this);
		return this;
	}

	_update() {
		if (this._disposed || !this._viewer) return;
		this.sprite.visible = this.visible && this._iconLoaded;
		if (!this.sprite.visible) {
			// 隐藏时保证高亮复位不粘连（_updateHover 不可达，且复位后
			// 清检测基准——重新可见时即使鼠标未动也会重新判定悬停）
			if (this._highlighted) this.setHighlighted(false);
			this._lastHoverMouse = null;
			// 同步退出手型集合：显隐开关关闭后原位置手型立即消失
			if (hoveredDepthAnnos.delete(this)) _refreshCursor(this._viewer);
			return;
		}

		if (this.fixedScreenSize) {
			// 屏幕恒定大小：按投影半径缩放（与 VolumeTool 的 label 缩放同款算法）。
			// sizeAttenuation=true 下世界 scale 随距离反比放大，屏显像素恒定，
			// 深度测试仍取 sprite 中心点——远处被地形挡住即整枚隐藏
			const camera = this._viewer.scene.getActiveCamera();
			const domElement = this._viewer.renderer.domElement;
			const distance = this.sprite.position.distanceTo(camera.position);
			if (distance <= 0) return;
			const pr = Utils.projectedRadius(1, camera, distance,
				domElement.clientWidth, domElement.clientHeight);
			if (!pr || pr <= 0) return;
			const scale = this.screenSize / pr;
			this.sprite.scale.set(scale * this.aspect, scale, 1); // 宽 = 高 × aspect
		} else {
			this.sprite.scale.set(this.worldSize * this.aspect, this.worldSize, 1);
		}

		// 缩放后检测：屏显模式下命中区域取决于最新 scale
		this._updateHover();
	}

	// 悬停反馈（类内闭环，方案 B）：InputHandler.onMouseMove 不向 inputListeners
	// 派发 mousemove，改为每帧对比 inputHandler.mouse（公开字段，mousemove 时
	// 更新）——变化时做一次单 sprite billboard 求交：命中换 iconActive + 手型
	// 光标，移开恢复。不动 InputHandler、不进 hoveredElements 拾取池
	_updateHover() {
		const inputHandler = this._viewer.inputHandler;
		if (!inputHandler || !inputHandler.mouse) return;
		const mouse = inputHandler.mouse;
		if (this._lastHoverMouse
			&& this._lastHoverMouse.x === mouse.x
			&& this._lastHoverMouse.y === mouse.y) {
			return; // 鼠标未动，零开销
		}
		this._lastHoverMouse = { x: mouse.x, y: mouse.y };

		const camera = this._viewer.scene.getActiveCamera();
		const domElement = this._viewer.renderer.domElement;
		const ray = Utils.mouseToRay(mouse, camera, domElement.clientWidth, domElement.clientHeight);
		const raycaster = new THREE.Raycaster(ray.origin, ray.direction);
		raycaster.camera = camera; // Sprite.raycast 必需（视空间 billboard 测试）
		const hit = raycaster.intersectObject(this.sprite, false).length > 0;

		// cursor 映射与换图解耦：硬件设备无 iconActive 也要手型提示可点击
		if (hit) {
			hoveredDepthAnnos.add(this);
		} else {
			hoveredDepthAnnos.delete(this);
		}
		_refreshCursor(this._viewer);

		// 换图仅在配置了 iconActive 时（无悬停图的实例只做 cursor）
		if (this._iconActive) {
			if (hit && !this._highlighted) {
				this.setHighlighted(true);
			} else if (!hit && this._highlighted) {
				this.setHighlighted(false);
			}
		}
	}

	// ---------------- 点击拾取（类内自管） ----------------

	_onMouseDown(e) {
		// 重置配对状态：任何 mousedown 都先清 _downScreen，mouseup 只响应配对的按下
		this._downScreen = null;
		// 显隐/纹理守卫：Raycaster 不跳过不可见对象（sprite.visible=false 挡不住
		// raycast），隐藏或未加载完的图标被点击会触发不可见副作用，必须在此拦截
		if (this._disposed || !this.visible || !this._iconLoaded || !this.sprite) return;
		if (this.actions.length === 0) return;
		this._downScreen = { x: e.mouse.x, y: e.mouse.y };
	}

	_onMouseUp(e) {
		if (this._disposed || !this.visible || !this._iconLoaded || !this.sprite) return;
		if (this.actions.length === 0 || !this._downScreen) return;
		const dx = e.mouse.x - this._downScreen.x;
		const dy = e.mouse.y - this._downScreen.y;
		this._downScreen = null;
		if (dx * dx + dy * dy > 25) return; // 移动超过 5px 视为拖拽，不算点击

		const camera = this._viewer.scene.getActiveCamera();
		const domElement = this._viewer.renderer.domElement;
		const ray = Utils.mouseToRay(e.mouse, camera, domElement.clientWidth, domElement.clientHeight);
		const raycaster = new THREE.Raycaster(ray.origin, ray.direction);
		// three.js：Sprite.raycast 需要把射线变换到视空间做 billboard 命中测试，
		// camera 为 null 时直接抛错（构造函数不接收 camera，必须显式赋值）
		raycaster.camera = camera;
		const hits = raycaster.intersectObject(this.sprite, false);
		if (hits.length === 0) return;

		// 遮挡检测：点云/高斯命中点比图标更近 = 图标被挡住，点击不派发
		if (this._isOccluded(e.mouse, camera, hits[0].distance)) return;

		for (const action of this.actions) {
			if (typeof action.onclick === 'function') action.onclick(this);
		}
	}

	_isOccluded(mouse, camera, spriteDistance) {
		const scene = this._viewer.scene;
		try {
			if (scene.pointclouds && scene.pointclouds.length > 0) {
				const pcHit = Utils.getMousePointCloudIntersection(mouse, camera, this._viewer, scene.pointclouds);
				if (pcHit && pcHit.distance < spriteDistance) return true;
			}
			if (scene.sceneShowModel !== ShowMode.PointClouds
				&& scene.gaussianSplats && scene.gaussianSplats.length > 0) {
				const gsHit = Utils.getMouseGaussianSplatIntersection(mouse, camera, this._viewer, scene.gaussianSplats);
				if (gsHit && gsHit.distance < spriteDistance) return true;
			}
		} catch (err) {
			console.warn('[AnnotationYdDepth] 点击遮挡检测失败，按可见处理', err);
		}
		return false;
	}

	// ---------------- 透明度 ----------------

	// 缺省 1 恢复全显；0.3 = 半透明选中反馈（对齐 AnnotationYd.setOpacity 语义）
	setOpacity(opacity = 1) {
		this.opacity = opacity;
		if (this.sprite && this.sprite.material) {
			this.sprite.material.opacity = opacity;
		}
	}

	// ---------------- 测点图三件套 ----------------

	// 惰性创建：测点图 Sprite + 名称 TextSprite 挂在主 sprite 下，
	// 位置/尺寸以"父图标 = 1 单位"的比例表达——父 scale 变化（屏显/世界模式）
	// 时子对象等比跟随，深度遮挡语义与主图标一致
	_ensurePointImgObjects() {
		if (this._pointImgSprite) return;

		const material = new THREE.SpriteMaterial({
			transparent: true,
			depthTest: true,
			depthWrite: false,
			sizeAttenuation: true,
		});
		this._pointImgSprite = new THREE.Sprite(material);
		this._pointImgSprite.name = 'annotation_yd_depth_pointimg';
		this._pointImgSprite.visible = false;
		this._pointImgSprite.position.set(0.95, 1.05, 0); // 主图标右上方
		this._pointImgSprite.scale.set(1.1, 1.1, 1);
		this.sprite.add(this._pointImgSprite);

		this._pointName = new TextSprite('');
		this._pointName.material.depthTest = true;  // 默认 false 会穿透遮挡，改回一致语义
		this._pointName.material.needsUpdate = true;
		this._pointName.setBackgroundColor({ r: 40, g: 40, b: 40, a: 0.85 });
		this._pointName.position.set(0.95, 1.85, 0);
		this.sprite.add(this._pointName);
	}

	// name = 测点名称（名称条），path = 实拍图 URL；图片异步加载完成后按
	// showPointImg 的状态决定显示
	setPointImg(params = {}) {
		if (this._disposed) return;
		this._ensurePointImgObjects();
		if (params.name != null) {
			this._pointName.setText(params.name);
		}
		if (params.path) {
			this._texLoader.load(params.path, (texture) => {
				if (this._disposed) {
					texture.dispose();
					return;
				}
				if (THREE.SRGBColorSpace !== undefined) texture.colorSpace = THREE.SRGBColorSpace;
				if (this._pointImgTexture) this._pointImgTexture.dispose();
				this._pointImgTexture = texture;
				this._pointImgSprite.material.map = texture;
				this._pointImgSprite.material.needsUpdate = true;
				this._pointImgSprite.visible = this._pointImgShown;
			}, undefined, (err) => {
				console.warn('[AnnotationYdDepth] 测点图加载失败', params.path, err);
			});
		}
	}

	showPointImg(shown) {
		this._pointImgShown = !!shown;
		if (this._pointImgSprite) {
			// 图片未就绪时仅记忆状态，加载回调里生效
			this._pointImgSprite.visible = this._pointImgShown && !!this._pointImgSprite.material.map;
		}
		if (this._pointName) {
			this._pointName.visible = this._pointImgShown;
		}
	}

	// ---------------- 高亮 / 显隐 / 销毁 ----------------

	// 高亮换图（对齐 AnnotationYd.setHighlighted 的使用习惯）
	setHighlighted(highlighted) {
		this._highlighted = !!highlighted;
		const material = this.sprite.material;
		const texture = this._highlighted
			? (this._iconActiveTexture || this._iconTexture)
			: this._iconTexture;
		if (material.map !== texture) {
			material.map = texture || null;
			material.needsUpdate = true;
		}
	}

	setVisible(visible) {
		this.visible = !!visible;
	}

	dispose() {
		if (this._disposed) return;
		this._disposed = true;
		// 退出手型集合并刷新（_viewer 置空前）：防止已销毁实例把光标卡在 pointer
		if (hoveredDepthAnnos.delete(this)) _refreshCursor(this._viewer);
		if (this._viewer) {
			if (this._updateListener) {
				this._viewer.removeEventListener("update", this._updateListener);
			}
			if (this._viewer.inputHandler) {
				this._viewer.inputHandler.removeInputListener(this);
			}
		}
		if (this._pointName) {
			if (this._pointName.sprite) {
				if (this._pointName.sprite.material) this._pointName.sprite.material.dispose();
				if (this._pointName.sprite.material && this._pointName.sprite.material.map) {
					this._pointName.sprite.material.map.dispose();
				}
			}
			this._pointName.removeFromParent();
			this._pointName = null;
		}
		if (this._pointImgSprite) {
			if (this._pointImgSprite.material) this._pointImgSprite.material.dispose();
			this._pointImgSprite.removeFromParent();
			this._pointImgSprite = null;
		}
		if (this._pointImgTexture) this._pointImgTexture.dispose();
		this._pointImgTexture = null;
		if (this.sprite.parent) {
			this.sprite.parent.remove(this.sprite);
		}
		if (this.sprite.material) {
			this.sprite.material.dispose();
		}
		if (this._iconTexture) this._iconTexture.dispose();
		if (this._iconActiveTexture) this._iconActiveTexture.dispose();
		this._iconTexture = null;
		this._iconActiveTexture = null;
		this.sprite = null;
		this._viewer = null;
		this._updateListener = null;
	}
}
